/**
 * Emerald Books — Phase 8 & 9 module pages.
 *
 * Lightweight but functional CRUD UIs for the newer modules. Design language
 * matches the existing Books app (light theme, rounded, emerald accents).
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, KeyRound, Copy, Trash2, Play, Webhook, Shield, DownloadCloud, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { booksClient, errMsg } from './api';

/* -------------------------------------------------------------------------- */
/* Generic placeholder for pages we haven't fleshed out yet                    */
/* -------------------------------------------------------------------------- */
export const ComingSoonPage = ({ title, hint, icon: Icon }) => (
  <div className="mx-auto max-w-2xl py-12 text-center" data-testid={`coming-soon-${title.replace(/\s+/g,'-').toLowerCase()}`}>
    {Icon && <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-600"><Icon className="h-7 w-7" /></div>}
    <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
    <p className="mx-auto mt-3 max-w-lg text-slate-500">
      The backend for this module is live and ready. The polished UI is ships next.
      {hint && <span className="mt-2 block text-xs text-slate-400">API: <code className="text-emerald-600">{hint}</code></span>}
    </p>
  </div>
);

/* -------------------------------------------------------------------------- */
/* API Keys                                                                    */
/* -------------------------------------------------------------------------- */
export const ApiKeysPage = () => {
  const [rows, setRows] = useState([]);
  const [rawKey, setRawKey] = useState(null);
  const [label, setLabel] = useState('');
  const load = () => booksClient.get('/api-keys').then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const issue = async () => {
    if (!label) return;
    try {
      const { data } = await booksClient.post('/api-keys', { label, scopes: ['read', 'write'] });
      setRawKey(data.raw_key);
      setLabel('');
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async (id) => {
    if (!confirm('Revoke this key? Apps using it will stop working.')) return;
    try { await booksClient.post(`/api-keys/${id}/revoke`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mx-auto max-w-4xl" data-testid="books-api-keys-page">
      <h1 className="text-3xl font-semibold tracking-tight">API Keys</h1>
      <p className="mt-1 text-sm text-slate-500">Issue keys for external systems to push invoices and read data via <code>/api/v1/*</code>.</p>
      <div className="mt-6 flex gap-2">
        <Input placeholder="Key label (e.g. Zapier prod)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="apikey-label-input" />
        <Button onClick={issue} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="apikey-issue-btn">
          <KeyRound className="mr-1 h-4 w-4" /> Issue key
        </Button>
      </div>
      {rawKey && (
        <div className="mt-4 rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4" data-testid="apikey-shown">
          <div className="mb-2 text-sm font-semibold text-emerald-800">Save this key — it will not be shown again.</div>
          <div className="flex items-center gap-2 rounded bg-white px-3 py-2 font-mono text-xs">
            <code className="flex-1 break-all">{rawKey}</code>
            <button onClick={() => { navigator.clipboard.writeText(rawKey); toast.success('Copied'); }}><Copy className="h-4 w-4" /></button>
          </div>
          <button className="mt-2 text-xs text-emerald-700 hover:underline" onClick={() => setRawKey(null)}>Dismiss</button>
        </div>
      )}
      <div className="mt-6 rounded-xl border bg-white">
        {rows.length === 0 ? <div className="p-8 text-center text-slate-400">No API keys yet.</div> :
          <table className="w-full text-sm"><thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr><th className="p-3 text-left">Label</th><th className="p-3 text-left">Preview</th><th className="p-3 text-left">Scopes</th><th className="p-3 text-left">Status</th><th className="p-3 text-right">Used</th><th></th></tr></thead>
          <tbody>{rows.map((k) => (
            <tr key={k.key_id} className="border-b last:border-b-0" data-testid={`apikey-row-${k.label.replace(/\s+/g,'-')}`}>
              <td className="p-3">{k.label}</td>
              <td className="p-3 font-mono text-xs">{k.key_preview}</td>
              <td className="p-3 text-xs">{(k.scopes || []).join(', ')}</td>
              <td className="p-3"><span className={'rounded-full px-2 py-0.5 text-xs ' + (k.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500')}>{k.status}</span></td>
              <td className="p-3 text-right text-xs">{k.usage_count || 0}</td>
              <td className="p-3">{k.status === 'active' && <button onClick={() => revoke(k.key_id)} className="text-rose-600 hover:text-rose-700" data-testid={`apikey-revoke-${k.label.replace(/\s+/g,'-')}`}><Trash2 className="h-4 w-4" /></button>}</td>
            </tr>
          ))}</tbody></table>}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Webhooks                                                                    */
/* -------------------------------------------------------------------------- */
export const WebhooksPage = () => {
  const [data, setData] = useState({ webhooks: [], available_events: [] });
  const [form, setForm] = useState({ url: '', events: ['*'] });
  const [secret, setSecret] = useState(null);
  const load = () => booksClient.get('/webhooks').then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      const r = await booksClient.post('/webhooks', form);
      setSecret(r.data.secret);
      setForm({ url: '', events: ['*'] });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const test = async (id) => { try { await booksClient.post(`/webhooks/${id}/test`, { event: 'invoice.sent' }); toast.success('Test fired'); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="mx-auto max-w-4xl" data-testid="books-webhooks-page">
      <h1 className="text-3xl font-semibold tracking-tight">Webhooks</h1>
      <p className="mt-1 text-sm text-slate-500">Get notified on every event. Payloads are signed with HMAC-SHA256.</p>
      <form onSubmit={create} className="mt-6 flex gap-2">
        <Input placeholder="https://your-app.com/webhooks/emerald" required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="webhook-url-input" />
        <Button type="submit" className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="webhook-add-btn"><Plus className="mr-1 h-4 w-4" />Add</Button>
      </form>
      {secret && (
        <div className="mt-4 rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4">
          <div className="mb-2 text-sm font-semibold text-emerald-800">Signing secret — save it now.</div>
          <div className="rounded bg-white px-3 py-2 font-mono text-xs">{secret}</div>
          <button onClick={() => setSecret(null)} className="mt-2 text-xs text-emerald-700">Dismiss</button>
        </div>
      )}
      <div className="mt-6 space-y-3">
        {data.webhooks.length === 0 && <div className="rounded-xl border bg-white p-8 text-center text-slate-400">No webhooks yet.</div>}
        {data.webhooks.map((h) => (
          <div key={h.webhook_id} className="flex items-center justify-between rounded-xl border bg-white p-4" data-testid={`webhook-row-${h.webhook_id}`}>
            <div><div className="font-medium">{h.url}</div><div className="text-xs text-slate-500">Events: {(h.events || []).join(', ')} · Delivered: {h.delivery_count || 0}</div></div>
            <Button variant="outline" size="sm" onClick={() => test(h.webhook_id)} data-testid={`webhook-test-${h.webhook_id}`}><Play className="mr-1 h-3 w-3" />Test</Button>
          </div>
        ))}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Backup                                                                      */
/* -------------------------------------------------------------------------- */
export const BackupPage = () => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const resp = await booksClient.get('/settings/backup/export', { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'emerald-books-backup.zip'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-2xl py-12 text-center" data-testid="books-backup-page">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-600"><DownloadCloud className="h-7 w-7" /></div>
      <h1 className="text-3xl font-semibold tracking-tight">Backup Export</h1>
      <p className="mx-auto mt-3 max-w-lg text-slate-500">Download a ZIP of every collection in your tenant as JSON. Store it anywhere.</p>
      <Button onClick={download} disabled={busy} className="mt-6 bg-emerald-600 text-white hover:bg-emerald-500" data-testid="backup-download-btn">
        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <DownloadCloud className="mr-1 h-4 w-4" />} Export & download
      </Button>
    </div>
  );
};
